import React, { useId, useRef } from 'react';
type RangeSliderProps = {
  label: string;
  unit: string;
  min: number;
  max: number;
  step?: number;
  value: number;
  onChange: (v: number) => void;
};
export function RangeSlider({
  label,
  unit,
  min,
  max,
  step = 1,
  value,
  onChange
}: RangeSliderProps) {
  const id = useId();
  const trackRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pct = (value - min) / (max - min) * 100;

  // Pointer handling is done on the track itself (instead of relying on the native
  // range thumb), so dragging works even when the phone is scaled or animated.
  const valueFromPointer = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return value;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const raw = min + ratio * (max - min);
    const stepped = Math.round((raw - min) / step) * step + min;
    return Math.min(max, Math.max(min, Number(stepped.toFixed(4))));
  };
  const update = (clientX: number) => {
    const next = valueFromPointer(clientX);
    if (next !== value) onChange(next);
  };
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    inputRef.current?.focus({
      preventScroll: true
    });
    update(e.clientX);
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
    update(e.clientX);
  };
  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };
  return <div data-id="element-785" data-mp="1p6jz0k98is7u">
      <div className="flex items-baseline justify-between" data-id="element-786" data-mp="1p3xp4dyvg0mg">
        <label htmlFor={id} className="font-mono text-[10.5px] tracking-[0.14em] text-graphite" data-id="element-787" data-mp="3vwztdlep8xqr">
          {label}
        </label>
        <p className="text-[20px] font-semibold tabular-nums tracking-[-0.02em] text-ink" data-id="element-788" data-mp="3qz84z1d9jtrb">
          {value}
          <span className="ml-1 text-[12px] font-medium text-graphite" data-id="element-789" data-mp="1uwbr7fu3omtj">{unit}</span>
        </p>
      </div>
      <div ref={trackRef} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp} className="relative mt-1 h-9 cursor-pointer touch-none select-none" data-id="element-790" data-mp="23und54k0uwfn">
        <div className="absolute inset-x-0 top-1/2 h-[4px] -translate-y-1/2 rounded-full bg-ink/10" data-id="element-791" data-mp="2dqqq75gewfhd" />
        <div className="absolute left-0 top-1/2 h-[4px] -translate-y-1/2 rounded-full bg-ink" style={{
        width: `${pct}%`
      }} data-id="element-792" data-mp="2ipl721utjtyu" />
        <div className="pointer-events-none absolute top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-ink bg-white shadow-[0_2px_6px_rgba(28,29,34,0.18)]" style={{
        left: `${pct}%`
      }} aria-hidden="true" data-id="element-793" data-mp="2rjzvov18ogum" />
        {/* Native input kept for keyboard and screen-reader support. */}
        <input ref={inputRef} id={id} type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(Number(e.target.value))} className="precision-range pointer-events-none absolute inset-0 h-full w-full" aria-valuetext={`${value} ${unit}`} data-id="element-794" data-mp="3bnxfo1ebo3sa" />
      </div>
      <div className="flex justify-between font-mono text-[9.5px] text-graphite/70" aria-hidden="true" data-id="element-795" data-mp="3ubsp592wivfl">
        <span data-id="element-796" data-mp="3pf25kbrw1hpf">{min}</span>
        <span data-id="element-797" data-mp="3pfu49vgs7vjx">{max}</span>
      </div>
    </div>;
}